import React, { useState } from 'react';
import { APPS_DATA } from '../data/mockData';
import { AppData } from '../types';
import { StoreBadge } from '../components/StoreBadge';
import { AppIcon } from '../components/AppIcon';
import { Scale, Plus, X, Star } from 'lucide-react';
import { getGrowthClassification } from '../components/GrowthScoreGauge';

interface CompareViewProps {
  initialAppIds?: string[];
  onSelectApp: (appId: string) => void;
}

export const CompareView: React.FC<CompareViewProps> = ({
  initialAppIds = ['duolingo', 'babbel', 'busuu'],
  onSelectApp
}) => {
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>(initialAppIds.slice(0, 5));
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDropdown, setShowAddDropdown] = useState(false);

  const allApps = Object.values(APPS_DATA);
  const comparedApps: AppData[] = selectedAppIds.map((id) => APPS_DATA[id]).filter(Boolean);

  const availableToAdd = allApps.filter((a) => !selectedAppIds.includes(a.id));

  const handleAddApp = (appId: string) => {
    if (selectedAppIds.length < 5 && !selectedAppIds.includes(appId)) {
      setSelectedAppIds([...selectedAppIds, appId]);
      setShowAddDropdown(false);
      setSearchQuery('');
    }
  };

  const handleRemoveApp = (appId: string) => {
    if (selectedAppIds.length > 1) {
      setSelectedAppIds(selectedAppIds.filter((id) => id !== appId));
    }
  };

  // Color palette for compare lines
  const colors = ['#1177ff', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-200/80">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold mb-2">
            <Scale className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Head-to-Head Benchmarking</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight">
            App Comparison Engine
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Compare growth velocity, rankings, estimated monetization, and review sentiment side-by-side (up to 5 apps).
          </p>
        </div>

        {/* Add App Button / Dropdown */}
        <div className="relative">
          {selectedAppIds.length < 5 ? (
            <div>
              <button
                onClick={() => setShowAddDropdown(!showAddDropdown)}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-2.5 rounded-2xl shadow-md transition-all flex items-center gap-2"
              >
                <Plus className="w-4 h-4" strokeWidth={2} />
                <span>Add App to Compare</span>
                <span className="text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded">
                  {selectedAppIds.length}/5
                </span>
              </button>

              {showAddDropdown && (
                <div className="absolute right-0 top-12 z-30 w-72 bg-white rounded-2xl p-3 shadow-2xl border border-slate-200 space-y-2 animate-scaleUp">
                  <input
                    type="text"
                    placeholder="Search app to add..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full text-xs p-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    autoFocus
                  />
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {availableToAdd
                      .filter((a) => a.name.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map((app) => (
                        <div
                          key={app.id}
                          onClick={() => handleAddApp(app.id)}
                          className="p-2 rounded-xl hover:bg-slate-50 flex items-center gap-2 cursor-pointer text-xs"
                        >
                          <AppIcon app={app} size="xs" className="w-6 h-6 rounded-lg flex-shrink-0" />
                          <span className="font-semibold text-slate-900 truncate">{app.name}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <span className="text-xs text-slate-400 font-semibold bg-slate-100 px-3 py-2 rounded-xl">
              Max 5 Apps Selected
            </span>
          )}
        </div>
      </div>

      {/* Selected App Badges Bar */}
      <div className="flex flex-wrap items-center gap-3">
        {comparedApps.map((app, idx) => (
          <div
            key={app.id}
            className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-2xl px-3 py-2 shadow-2xs"
          >
            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx] }} />
            <AppIcon app={app} size="xs" className="w-7 h-7 rounded-xl flex-shrink-0" />
            <span className="text-xs font-bold text-slate-900">{app.name.split(':')[0]}</span>
            {selectedAppIds.length > 1 && (
              <button
                onClick={() => handleRemoveApp(app.id)}
                className="text-slate-400 hover:text-rose-600 p-0.5 rounded-md hover:bg-rose-50 transition-colors"
                title="Remove"
              >
                <X className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Overlaid Trend Simulation Visual */}
      <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">Overlaid Ranking Trajectory (Last 30 Days)</h3>
          <span className="text-xs text-slate-400">Lower rank is better (#1 is top)</span>
        </div>

        <div className="h-56 w-full relative">
          <svg viewBox="0 0 600 200" className="w-full h-full overflow-visible">
            {/* Grid lines */}
            {[20, 60, 100, 140, 180].map((y) => (
              <line key={y} x1="0" y1={y} x2="600" y2={y} stroke="#f1f5f9" strokeWidth="1" strokeDasharray="3 3" />
            ))}

            {/* Path for each app */}
            {comparedApps.map((app, idx) => {
              const points = app.rankingsHistory['30D'];
              if (!points || points.length === 0) return null;
              const pathD = points
                .map((p, i) => {
                  const x = (i / (points.length - 1)) * 600;
                  const y = 20 + Math.min(160, p.rank * 3.5);
                  return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                })
                .join(' ');

              return (
                <g key={app.id}>
                  <path
                    d={pathD}
                    fill="none"
                    stroke={colors[idx]}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              );
            })}
          </svg>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-6 text-xs font-semibold pt-2 border-t border-slate-100">
          {comparedApps.map((app, idx) => (
            <div key={app.id} className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[idx] }} />
              <span className="text-slate-800">{app.name.split(':')[0]}</span>
              <span className="text-slate-400 font-normal">#{app.currentRank}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Side-by-Side Comparison Matrix */}
      <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm overflow-hidden">
        <h3 className="text-base font-bold text-slate-900 mb-4">Metric Comparison Matrix</h3>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-100 text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
                <th className="pb-3 pl-2 w-48">Metric</th>
                {comparedApps.map((app, idx) => (
                  <th key={app.id} className="pb-3 text-center min-w-[140px]">
                    <div className="flex flex-col items-center gap-1">
                      <AppIcon app={app} size="xs" className="w-8 h-8 rounded-xl shadow-2xs flex-shrink-0" />
                      <span className="font-bold text-slate-900 text-xs line-clamp-1">{app.name.split(':')[0]}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {/* Category */}
              <tr className="hover:bg-slate-50/60">
                <td className="py-3.5 pl-2 font-bold text-slate-500">Category</td>
                {comparedApps.map((app) => (
                  <td key={app.id} className="py-3.5 text-center text-slate-800">
                    {app.category}
                  </td>
                ))}
              </tr>

              {/* Current Rank */}
              <tr className="hover:bg-slate-50/60">
                <td className="py-3.5 pl-2 font-bold text-slate-500">Current Rank</td>
                {comparedApps.map((app) => (
                  <td key={app.id} className="py-3.5 text-center">
                    <strong className="text-sm text-slate-900 font-black">#{app.currentRank}</strong>
                  </td>
                ))}
              </tr>

              {/* Growth Score */}
              <tr className="hover:bg-slate-50/60">
                <td className="py-3.5 pl-2 font-bold text-slate-500">Growth Score (0-100)</td>
                {comparedApps.map((app) => {
                  const { label, bg } = getGrowthClassification(app.growthScore);
                  return (
                    <td key={app.id} className="py-3.5 text-center">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${bg}`}>
                        {app.growthScore} • {label}
                      </span>
                    </td>
                  );
                })}
              </tr>

              {/* Rating */}
              <tr className="hover:bg-slate-50/60">
                <td className="py-3.5 pl-2 font-bold text-slate-500">Rating & Reviews</td>
                {comparedApps.map((app) => (
                  <td key={app.id} className="py-3.5 text-center">
                    <span className="font-bold text-amber-500 inline-flex items-center gap-1 justify-center">
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                      <span>{app.rating.toFixed(1)}</span>
                    </span>
                    <span className="text-[10px] text-slate-400 block">({(app.totalReviews / 1000).toFixed(0)}K)</span>
                  </td>
                ))}
              </tr>

              {/* Estimated Monthly Downloads */}
              <tr className="hover:bg-slate-50/60 bg-blue-50/10">
                <td className="py-3.5 pl-2 font-bold text-blue-700 flex items-center gap-1">
                  <span>Est. Monthly Downloads</span>
                  <span className="text-[9px] bg-blue-100 text-blue-800 px-1 rounded font-bold">EST</span>
                </td>
                {comparedApps.map((app) => (
                  <td key={app.id} className="py-3.5 text-center font-bold text-slate-900">
                    {(app.estimatedMonthlyDownloads / 1000000).toFixed(1)}M / mo
                  </td>
                ))}
              </tr>

              {/* Estimated Revenue */}
              <tr className="hover:bg-slate-50/60 bg-emerald-50/10">
                <td className="py-3.5 pl-2 font-bold text-emerald-700 flex items-center gap-1">
                  <span>Est. Monthly Revenue</span>
                  <span className="text-[9px] bg-emerald-100 text-emerald-800 px-1 rounded font-bold">EST</span>
                </td>
                {comparedApps.map((app) => (
                  <td key={app.id} className="py-3.5 text-center font-bold text-emerald-700">
                    ${(app.estimatedMonthlyRevenueUsd / 1000000).toFixed(1)}M
                  </td>
                ))}
              </tr>

              {/* Update Frequency */}
              <tr className="hover:bg-slate-50/60">
                <td className="py-3.5 pl-2 font-bold text-slate-500">Update Cadence</td>
                {comparedApps.map((app) => (
                  <td key={app.id} className="py-3.5 text-center text-slate-700">
                    Avg {app.averageUpdateDays} days
                  </td>
                ))}
              </tr>

              {/* Positive Sentiment */}
              <tr className="hover:bg-slate-50/60">
                <td className="py-3.5 pl-2 font-bold text-slate-500">Positive Sentiment</td>
                {comparedApps.map((app) => (
                  <td key={app.id} className="py-3.5 text-center text-emerald-600 font-bold">
                    {app.sentiment.positive}%
                  </td>
                ))}
              </tr>

              {/* Action row */}
              <tr>
                <td className="py-4 pl-2 font-bold text-slate-400">Deep Profile</td>
                {comparedApps.map((app) => (
                  <td key={app.id} className="py-4 text-center">
                    <button
                      onClick={() => onSelectApp(app.id)}
                      className="text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Open {app.name.split(':')[0]} ›
                    </button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
